import { useCallback, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import {
  Button,
  Card,
  CardContent,
  IndicateurSauvegarde,
  Loader,
  QsortBoard,
  QsortConfirmation,
  useSauvegardeDifferee,
  type QsortItem,
} from "@repo/ui";
import {
  estComplet,
  libellesNiveauxQsort,
  melangerDeterministe,
  placementsVides,
  resumeNiveauxIncomplets,
  type PlacementsQsort,
} from "@repo/utils";
import { api } from "@convex/_generated/api";
import { TemplateFormEnum, TypeEvalEnum } from "@convex/schema";
import { scoreDuNiveau } from "@convex/referentiel/qsort";
import {
  type FormTemplateProps,
  isBackendError,
  type FitCulturelReponses,
} from "../../lib/types";

/** Délai d'autosave — assez court pour ne rien perdre, assez long pour ne pas
 *  écrire à chaque déplacement d'item. Aligné sur l'écran référent. */
const DELAI_SAUVEGARDE_MS = 800;

type DonneesFitCulturel = {
  type: typeof TypeEvalEnum.FORM;
  template: typeof TemplateFormEnum.FIT_CULTUREL;
  reponses: FitCulturelReponses;
};

/**
 * Passation candidat du fit-culturel (plan §7.2).
 *
 * Même tableau Q-sort que le référent (`QsortBoard`, §7.1) et **mêmes items** —
 * ceux du snapshot de la campagne, pas du socle courant. Seule la consigne change :
 * le candidat classe ce qui compte pour LUI, le référent ce qui compte pour réussir
 * dans le poste (§7.3). Deux échelles différentes rendraient les écarts
 * ininterprétables.
 */
export function CandidateFitCulturelTemplate({
  token,
  parcoursId,
  editable,
  evaluation,
  onComplete,
}: FormTemplateProps) {
  const questionnaire = useQuery(
    api.referentiel.passation.obtenirQuestionnaireFitCulturelCandidat,
    { evaluationId: evaluation._id, parcoursId, token },
  );
  const submitEvaluation = useMutation(api.evaluations.soumettreEvaluation);
  const updateResponses = useMutation(api.evaluations.mettreAJourReponses);

  const [placements, setPlacements] = useState<PlacementsQsort | null>(null);
  const [confirmation, setConfirmation] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  // La mutation renvoie `{ erreur }` au lieu de lever : on la convertit, sans
  // quoi l'indicateur afficherait « Enregistré » sur un refus.
  const sauverReponses = useCallback(
    async (donnees: DonneesFitCulturel) => {
      const resultat = await updateResponses({
        evaluationId: evaluation._id,
        parcoursId,
        token,
        donnees,
      });
      if (isBackendError(resultat)) throw new Error(resultat.erreur);
    },
    [updateResponses, evaluation._id, parcoursId, token],
  );
  const autosave = useSauvegardeDifferee(sauverReponses, DELAI_SAUVEGARDE_MS);

  if (questionnaire === undefined) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader />
      </div>
    );
  }

  if ("erreur" in questionnaire) {
    return (
      <Card className="container mx-auto my-8">
        <CardContent className="pt-6 text-destructive">
          {questionnaire.erreur}
        </CardContent>
      </Card>
    );
  }

  const { config } = questionnaire;
  const pyramide =
    config.echelle.kind === "qsort" ? config.echelle.pyramide : [];
  const nbNiveaux = pyramide.length;
  const items: QsortItem[] = config.dimensions.map((dimension) => ({
    code: dimension.code,
    label: dimension.label,
    description: dimension.description,
  }));
  const libelles = libellesNiveauxQsort(nbNiveaux);

  // Reprise : les réponses déjà enregistrées repeuplent la pyramide. Un niveau
  // devenu hors pyramide (config modifiée entre-temps) est ignoré plutôt que de
  // casser l'écran.
  const etat =
    placements ??
    (() => {
      const initial = placementsVides(config.dimensions.map((d) => d.code));
      const reponses = evaluation.donnees.reponses as FitCulturelReponses;
      for (const reponse of Object.values(reponses)) {
        if (!(reponse.name in initial)) continue;
        if (
          !Number.isInteger(reponse.niveau) ||
          reponse.niveau < 0 ||
          reponse.niveau >= nbNiveaux
        ) {
          continue;
        }
        initial[reponse.name] = reponse.niveau;
      }
      return initial;
    })();

  /**
   * `score` est dupliqué depuis `niveau` pour qu'une restitution ancienne se
   * relise sans la pyramide de sa campagne (§1.3). Le matching, lui, recalcule
   * toujours depuis `niveau`.
   */
  const buildDonnees = (courant: PlacementsQsort): DonneesFitCulturel => ({
    type: TypeEvalEnum.FORM as const,
    template: TemplateFormEnum.FIT_CULTUREL,
    reponses: Object.entries(courant).reduce<FitCulturelReponses>(
      (acc, [code, niveau]) => {
        if (niveau === null) return acc;
        const item = items.find((i) => i.code === code);
        return {
          ...acc,
          [code]: {
            name: code,
            label: item?.label ?? code,
            niveau,
            score: scoreDuNiveau(niveau, nbNiveaux),
          },
        };
      },
      {},
    ),
  });

  const modifier = (suivant: PlacementsQsort) => {
    setPlacements(suivant);
    if (editable) autosave.planifier(buildDonnees(suivant));
  };

  const soumettre = async () => {
    // L'envoi porte l'état complet : l'autosave en attente n'a plus d'objet.
    autosave.annuler();
    setErreur(null);
    setEnCours(true);
    try {
      const resultat = await submitEvaluation({
        evaluationId: evaluation._id,
        parcoursId,
        token,
        donnees: buildDonnees(etat),
      });
      if (isBackendError(resultat)) {
        setErreur(resultat.erreur);
        return;
      }
      setConfirmation(false);
      onComplete?.();
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e));
    } finally {
      setEnCours(false);
    }
  };

  const complet = estComplet(etat, pyramide);
  const resteAClasser = resumeNiveauxIncomplets(etat, pyramide, libelles);

  return (
    <div className="container mx-auto space-y-6 py-8">
      <div className="space-y-3">
        <h1 className="text-2xl font-bold">Ce qui compte pour vous au travail</h1>
        {editable ? (
          <>
            <p className="text-muted-foreground">
              Classez ces éléments selon ce qui compte le plus{" "}
              <strong className="font-semibold text-foreground">pour vous</strong>{" "}
              dans votre travail — pas selon ce que vous imaginez attendu.
            </p>
            <p className="text-muted-foreground">
              Chaque niveau a un nombre de places{" "}
              <strong className="font-semibold text-foreground">imposé</strong> :
              vous devez donc trancher. Il n'y a pas de bonne réponse, seulement la
              vôtre.
            </p>
          </>
        ) : (
          <p className="text-muted-foreground">
            Votre classement a été envoyé et n'est plus modifiable. Le voici
            tel que vous l'avez laissé.
          </p>
        )}
      </div>

      {!editable && complet && (
        <PremiersNiveaux
          items={melangerDeterministe(items, evaluation._id)}
          placements={etat}
          libelles={libelles}
          nbNiveaux={Math.min(2, nbNiveaux)}
        />
      )}

      <Card>
        <CardContent>
          <QsortBoard
            items={items}
            pyramide={pyramide}
            placements={etat}
            onChange={modifier}
            libellesNiveaux={libelles}
            lectureSeule={!editable}
            graineOrdre={evaluation._id}
          />
        </CardContent>
      </Card>

      {editable && (
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
          <IndicateurSauvegarde
            etat={autosave.etat}
            onReessayer={autosave.reessayer}
          />
          <div className="flex flex-wrap items-center justify-end gap-3">
            {resteAClasser && (
              <p className="text-sm text-muted-foreground">{resteAClasser}</p>
            )}
            <Button
              // Sous le pouce sur mobile : pleine largeur, 44 px.
              className="min-h-11 w-full sm:min-h-9 sm:w-auto"
              disabled={enCours || !complet}
              onClick={() => {
                setErreur(null);
                setConfirmation(true);
              }}
            >
              Valider mes réponses
            </Button>
          </div>
        </div>
      )}

      <QsortConfirmation
        ouvert={confirmation}
        onFermer={() => setConfirmation(false)}
        onConfirmer={() => void soumettre()}
        enCours={enCours}
        erreur={erreur}
        items={items}
        pyramide={pyramide}
        placements={etat}
        libellesNiveaux={libelles}
        graineOrdre={evaluation._id}
        libelleConfirmer="Envoyer mes réponses"
      />
    </div>
  );
}

/**
 * Une fois le classement envoyé, le candidat retrouve en tête ce qu'il a placé
 * tout en haut. C'est l'exercice le plus personnel du parcours : le finir sur
 * un tableau figé ne lui rendait rien de ce qu'il venait de dire.
 */
function PremiersNiveaux({
  items,
  placements,
  libelles,
  nbNiveaux,
}: {
  items: QsortItem[];
  placements: PlacementsQsort;
  libelles: string[];
  nbNiveaux: number;
}) {
  return (
    <section
      aria-labelledby="fit-culturel-premiers"
      className="space-y-3 rounded-surface border border-charte-line-accent bg-charte-hover-opaque p-5"
    >
      <h2 id="fit-culturel-premiers" className="text-base font-semibold">
        Ce qui compte le plus pour vous
      </h2>
      <dl className="space-y-2">
        {Array.from({ length: nbNiveaux }, (_, niveau) => (
          <div key={niveau} className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <dt className="w-32 shrink-0 text-sm font-semibold text-charte-accent-light">
              {libelles[niveau]}
            </dt>
            <dd className="text-sm">
              {items
                .filter((item) => placements[item.code] === niveau)
                .map((item) => item.label)
                .join(" · ")}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
